/** サポートコンソール: 添付アップロード・メッセージ埋め込み */

export type SupportAttachment = {
  path: string;
  url: string;
  type: string;
  name: string;
};

export const MAX_REPLY_ATTACHMENTS = 5;

/** ファイル選択ダイアログ用（画像/動画 + オフィス/CSV/PDF） */
export const ACCEPT_ATTACHMENTS =
  "image/*,video/*,.pdf,.csv,.tsv,.txt,.xls,.xlsx,.xlsm,.doc,.docx,.ppt,.pptx";

const DOCUMENT_EXTS = new Set([
  ".pdf", ".csv", ".tsv", ".txt", ".xls", ".xlsx", ".xlsm", ".doc", ".docx", ".ppt", ".pptx",
]);

const DOCUMENT_MIMES = new Set([
  "application/pdf",
  "text/csv",
  "text/plain",
  "application/csv",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
]);

export function fileExt(name: string): string {
  const i = name.lastIndexOf(".");
  return i >= 0 ? name.slice(i).toLowerCase() : "";
}

export function isAttachableFile(file: File): boolean {
  const t = (file.type || "").split(";")[0].trim().toLowerCase();
  if (t.startsWith("image/") || t.startsWith("video/")) return true;
  const ext = fileExt(file.name);
  if (DOCUMENT_EXTS.has(ext)) return true;
  if (DOCUMENT_MIMES.has(t) && ext) return true;
  return false;
}

/** クリップボード貼付は画像/動画のみ（ファイルピッカーで CSV 等を選ぶ） */
export function isMediaFile(file: File): boolean {
  const t = (file.type || "").toLowerCase();
  return t.startsWith("image/") || t.startsWith("video/");
}

export function filesFromClipboard(data: DataTransfer | null): File[] {
  if (!data) return [];
  const out: File[] = [];
  for (const item of Array.from(data.items)) {
    if (item.kind !== "file") continue;
    const f = item.getAsFile();
    if (f && isMediaFile(f)) out.push(f);
  }
  return out;
}

/** ドラッグ&ドロップ用（ファイルピッカーと同じ拡張子） */
export function filesFromDataTransfer(data: DataTransfer | null): File[] {
  if (!data) return [];
  const out: File[] = [];
  const seen = new Set<string>();
  const push = (f: File | null) => {
    if (!f || !isAttachableFile(f)) return;
    const key = `${f.name}:${f.size}:${f.lastModified}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(f);
  };
  if (data.files?.length) {
    for (const f of Array.from(data.files)) push(f);
    return out;
  }
  for (const item of Array.from(data.items)) {
    if (item.kind !== "file") continue;
    push(item.getAsFile());
  }
  return out;
}

export function attachmentPreviewKind(file: File): "image" | "video" | "document" {
  const t = (file.type || "").toLowerCase();
  if (t.startsWith("video/")) return "video";
  if (t.startsWith("image/")) return "image";
  return "document";
}

export function formatAttachmentForMessage(att: SupportAttachment): string {
  const url = att.url.startsWith("/") ? att.url : att.url;
  if (att.type === "video") return `[🎬 ${att.name}](${url})`;
  if (att.type === "document") return `[📎 ${att.name}](${url})`;
  return `![${att.name}](${url})`;
}

export async function uploadTicketAttachment(ticketId: number, file: File): Promise<SupportAttachment> {
  const fd = new FormData();
  fd.append("file", file);
  const res = await fetch(`/api/support/tickets/${ticketId}/attachment`, { method: "POST", body: fd });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `HTTP ${res.status}`);
  }
  return (await res.json()) as SupportAttachment;
}

export async function uploadTicketAttachments(ticketId: number, files: File[]): Promise<SupportAttachment[]> {
  const uploaded: SupportAttachment[] = [];
  for (const file of files) {
    uploaded.push(await uploadTicketAttachment(ticketId, file));
  }
  return uploaded;
}
